import { urlUtility } from "@repo/utilities/url.utility.ts";
import {
  createSearchParams,
  NavigateOptions,
  useLocation,
  useNavigate,
  useParams,
  useSearchParams,
} from "react-router-dom";

export default function useRouterHandlerHook() {
  // url queries
  const navigate = useNavigate();
  const route = useParams();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const urlQueries = urlUtility.getUrlQueries(location.search);

  // FUNCTIONS
  const navigateTo = ({
    url,
    search,
    deleteSearchKey,
    replaceQueries = false,
    ...options
  }: {
    url?: string;
    search?: Record<string, string>;
    deleteSearchKey?: string | string[];
    replaceQueries?: boolean;
  } & NavigateOptions) => {
    const nextSearchQueries = replaceQueries ? {} : { ...urlQueries };

    if (deleteSearchKey) {
      if (Array.isArray(deleteSearchKey)) {
        deleteSearchKey.forEach((keyString) => {
          delete nextSearchQueries[keyString];
        });
      } else {
        delete nextSearchQueries[deleteSearchKey];
      }
    }

    navigate(
      {
        pathname: url,
        search: createSearchParams(
          replaceQueries
            ? (search ?? {})
            : {
                ...nextSearchQueries,
                ...search,
              }
        ).toString(),
      },
      options
    );
  };

  const navigateBackTo = ({
    searchKey,
    ...options
  }: { searchKey: string | string[] } & NavigateOptions) => {
    const urlObject = urlQueries;

    if (Array.isArray(searchKey)) {
      searchKey.forEach((keyString) => {
        delete urlObject[keyString];
      });
    } else {
      delete urlObject[searchKey];
    }

    navigate(
      {
        search: createSearchParams({
          ...urlObject,
        }).toString(),
      },
      options
    );
  };

  function addSearchParams({
    search,
    ...options
  }: {
    search: Record<string, string | string[]>;
  } & NavigateOptions) {
    navigate(
      {
        search: createSearchParams({
          ...urlQueries,
          ...search,
        }).toString(),
      },
      options
    );
  }

  // VARIABLES
  const activeRoutes = location.pathname?.split("/");
  const subRoute = activeRoutes[activeRoutes.length - 1];

  return {
    searchParams,
    setSearchParams,
    activeRoutes,
    subRoute,
    route,
    location,
    urlQueries,
    navigate,
    navigateTo,
    navigateBackTo,
    addSearchParams,
  };
}
